import { Link } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import type { CatalogCardProps } from '@/features/publicsite/types/catalog/CatalogCardProps';

/**
 * One card in a public catalogue grid.
 *
 * Five templates — trainings, funding, research, services, community — each spelled out the same
 * shape: a badge, a heading, a muted subtitle, then free lines. They differed only in which field
 * went where, which is why five copies of the markup existed and why a change to the card had to
 * be made five times.
 */
export function CatalogCard({
  badge,
  title,
  subtitle,
  children,
  id,
  to,
  href,
  linkLabel,
}: CatalogCardProps) {
  const body = (
    <>
      {badge && <span className="badge">{badge}</span>}
      <h3>{title}</h3>
      {subtitle && <p className="muted">{subtitle}</p>}
      {children}
      {href && (
        <span className="catalog-card__link">
          <span>{linkLabel ?? href}</span>
          <Icon name="arrow-right" className="link-arrow" />
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <a className="card" id={id} href={href} target="_blank" rel="noreferrer">
        {body}
      </a>
    );
  }

  return to ? (
    <Link className="card" id={id} to={to}>
      {body}
    </Link>
  ) : (
    // tabIndex on an anchored card, so the page that owns it can move the caret there and not
    // only the viewport: a fragment link that scrolls but leaves focus at the top of the document
    // has served the sighted reader and nobody else. -1 keeps it out of the tab order, which is
    // where a card that is not itself a link belongs.
    <div className="card" id={id} tabIndex={id ? -1 : undefined}>
      {body}
    </div>
  );
}
